import { Children, useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function PlanCarousel({
  children,
  initialIndex = 2, // Growth (Free 0, Starter 1, Growth 2, Pro 3)
  labels = [],
}) {
  const boxRef = useRef(null);
  const [active, setActive] = useState(initialIndex);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const count = Children.count(children);

  const scrollToIndex = useCallback((i, smooth = true) => {
    const box = boxRef.current;
    const card = box?.children[i];
    if (!box || !card) return;
    const left = card.offsetLeft - (box.clientWidth - card.offsetWidth) / 2;
    box.scrollTo({ left: Math.max(0, left), behavior: smooth ? "smooth" : "auto" });
  }, []);

  // Works out which card is centred and whether more cards exist on either
  // side. On desktop several cards are visible at once, so the first/last
  // card can never reach the centre -- the scroll edges decide instead.
  const syncScrollState = useCallback(() => {
    const box = boxRef.current;
    if (!box) return;
    const atStart = box.scrollLeft <= 4;
    const atEnd = box.scrollLeft + box.clientWidth >= box.scrollWidth - 4;
    setCanPrev(!atStart);
    setCanNext(!atEnd);

    if (atStart) {
      setActive(0);
      return;
    }
    if (atEnd) {
      setActive(count - 1);
      return;
    }
    const mid = box.scrollLeft + box.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    Array.from(box.children).forEach((c, i) => {
      const d = Math.abs(c.offsetLeft + c.offsetWidth / 2 - mid);
      if (d < bestDist) {
        best = i;
        bestDist = d;
      }
    });
    setActive(best);
  }, [count]);

  // Open with Growth centred on every screen size, then sync the arrows.
  useEffect(() => {
    scrollToIndex(initialIndex, false);
    syncScrollState();
    window.addEventListener("resize", syncScrollState);
    return () => window.removeEventListener("resize", syncScrollState);
  }, [initialIndex, scrollToIndex, syncScrollState]);

  // Arrows: move by exactly one card (width + gap).
  const step = (dir) => {
    const box = boxRef.current;
    const card = box?.children[0];
    if (!box || !card) return;
    const second = box.children[1];
    const gap = second ? second.offsetLeft - card.offsetLeft - card.offsetWidth : 0;
    box.scrollBy({ left: dir * (card.offsetWidth + gap), behavior: "smooth" });
  };

  // Big arrows, always visible on mobile AND desktop. They sit on the outer
  // edge of the carousel (half hanging outside on desktop) so they cover the
  // peeking neighbour card / card padding, never the readable text.
  // At the first/last plan they stay visible but dimmed.
  const arrowClass =
    "pointer-events-auto grid h-12 w-12 place-items-center rounded-full border border-ink/10 bg-white/95 text-ink shadow-xl backdrop-blur transition hover:scale-105 hover:bg-white active:scale-95 disabled:cursor-default disabled:opacity-40 disabled:hover:scale-100 md:h-16 md:w-16";

  return (
    <>
      <div className="relative">
        <div
          ref={boxRef}
          onScroll={syncScrollState}
          className="relative -mx-6 flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pb-8 pt-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:gap-6"
        >
          {children}
        </div>

        {/* Overlay spans the full carousel height; the row inside is sticky, so
            the arrows follow the middle of the screen while the user scrolls
            up/down past the cards. */}
        <div className="pointer-events-none absolute inset-0 z-20">
          <div
            className="sticky flex items-center justify-between"
            style={{ top: "calc(50vh - 32px)" }}
          >
            <button
              type="button"
              onClick={() => step(-1)}
              disabled={!canPrev}
              aria-label="Previous plan"
              className={`${arrowClass} -ml-4 md:-ml-8`}
            >
              <ChevronLeft className="h-7 w-7 md:h-9 md:w-9" strokeWidth={2.75} />
            </button>
            <button
              type="button"
              onClick={() => step(1)}
              disabled={!canNext}
              aria-label="Next plan"
              className={`${arrowClass} -mr-4 md:-mr-8`}
            >
              <ChevronRight className="h-7 w-7 md:h-9 md:w-9" strokeWidth={2.75} />
            </button>
          </div>
        </div>
      </div>

      <div className="mt-1 flex justify-center gap-2" role="tablist" aria-label="Plans">
        {Array.from({ length: count }).map((_, i) => (
          <button
            key={i}
            type="button"
            role="tab"
            aria-selected={active === i}
            aria-label={labels[i] ? `Show ${labels[i]} plan` : `Show plan ${i + 1}`}
            onClick={() => scrollToIndex(i)}
            className={`h-2 rounded-full transition-all ${
              active === i ? "w-6 bg-hotpink" : "w-2 bg-ink/20"
            }`}
          />
        ))}
      </div>
    </>
  );
}
